import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Grid, IconMapPin, Text, csx } from '@vtex/admin-ui'

import { DrawerFooter, DrawerBody } from '../../../../../../ui/components'
import { MethodSelectItem } from '../MethodSelectItem'
import type { Package } from '../../../../../../core/typings'
import { DeliveryChannel } from '../../../../constants'
import { formatAddressToExtended } from '../../../../utils'
import { useVendor } from '../../../../../../core/globalState/vendor'
import { ForceTakeAwayAlert } from './TakeAwayAlert'
import { StepSubmitButton } from '../../../../components'

const getCurrentStoreAddress = (store: Store | null) => {
  if (!store) {
    return ''
  }

  return formatAddressToExtended({
    city: store.city,
    number: store.number,
    state: store.state,
    neighborhood: store.neighborhood,
    postalCode: store.postalCode,
    street: store.address,
  })
}

export type TakeAwayContentProps = {
  currentPackage: Package
  onDeliveryMethodSubmit: (selectedDeliveryMethodSla: SLA) => void
  isOpen: boolean
  item: Package['items'][0]
  onSubmit: (itemIndex: number) => void
  packageHasTakeAwayAvailable: boolean
}

export const TakeAwayContent = ({
  currentPackage,
  isOpen,
  item,
  onSubmit,
  packageHasTakeAwayAvailable,
}: TakeAwayContentProps) => {
  const isTakeAwaySelected =
    currentPackage.selectedSlaType === DeliveryChannel.TakeAway

  const [isSelected, setIsSelected] = useState(isTakeAwaySelected)
  const { vendor } = useVendor()

  const handleMethodItemClick = () => {
    setIsSelected(true)
  }

  const handleDeliveryMethodSelectionSubmit = async () => {
    onSubmit(item.index)
  }

  useEffect(() => {
    if (!isOpen) {
      setIsSelected(isTakeAwaySelected)
    }
  }, [isOpen, isTakeAwaySelected])

  return (
    <>
      <DrawerBody>
        <Grid rowGap="1rem">
          {!packageHasTakeAwayAvailable && <ForceTakeAwayAlert />}

          <MethodSelectItem
            icon={<IconMapPin />}
            title={vendor.store?.name ?? ''}
            selected={isSelected}
            onClick={handleMethodItemClick}
            middleColumn={
              <Text>
                <Text tone="secondary" variant="detail">
                  {getCurrentStoreAddress(vendor.store)}
                </Text>
                <Text
                  variant="action2"
                  tone="positive"
                  className={csx({ marginTop: '$space-2', display: 'block' })}
                >
                  <FormattedMessage id="checkout.change-shipping-method-drawer-takeaway-time-estimate" />
                </Text>
              </Text>
            }
            rightColumn={
              <p
                className={csx({
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  color: isSelected ? '$blue40' : '$primary',
                })}
              >
                <FormattedMessage id="checkout.change-shipping-method-drawer-current-store" />
              </p>
            }
          />
        </Grid>
      </DrawerBody>
      <DrawerFooter>
        <StepSubmitButton
          disabled={!isSelected}
          onClick={handleDeliveryMethodSelectionSubmit}
          data-testid="take-away-content-submit"
        >
          <FormattedMessage
            id={
              !packageHasTakeAwayAvailable
                ? 'checkout.change-shipping-method-drawer-change-anyway-button'
                : 'continue'
            }
          />
        </StepSubmitButton>
      </DrawerFooter>
    </>
  )
}
